/* eslint-disable react/prop-types */
import Button from '@viking-eng/button';
import FieldValidationError from '@viking-eng/field-validation-error';
import ChevronDown from '@viking-eng/icon/lib/ChevronDown';
import Close from '@viking-eng/icon/lib/Close';
import PropTypes from '@viking-eng/prop-types';
import { requiredLabel } from '@viking-eng/utils';
import classNames from 'classnames';
import get from 'lodash/get';
import React, { useState } from 'react';
import SelectDropdown, { components, createFilter } from 'react-select';
import { CheckboxOption, DropdownIndicator, MultiValue, Option, SingleValue } from './Components';
import './Select.css';

// disabled: Disable the select
// errorCodes: Errorcodes (Required/Invalid)
// errorMessages: Error Messages from Redux
// input: Redux form input props
// meta: state of this field that redux-form tracking
// options: Options to appear in select (value & label) pair
// placeholder: Placeholder label (example:*country)
const Select = ({
  alt,
  appearanceInputText,
  ariaLabel,
  blurInputOnSelect,
  closeMenuOnSelect,
  dataId,
  disabled,
  errorCodes,
  errorMessages,
  helperLabel,
  hideError,
  input,
  inputId,
  isClearable,
  isMulti,
  isSearchable,
  matchFrom,
  meta: { active, error, touched, warning },
  multipleSelectedMessage,
  noOptionsMessage,
  onMenuOpen,
  openMenuOnFocus,
  options,
  placeholder,
  reactSelectProps,
  readOnly,
  required,
  strong,
  tabSelectsValue,
  title,
  validationErrorTabIndex,
  valueSeparator,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { onKeyUp, onBlur, onChange, onFocus, value } = input;
  const showError = touched && !active && error && !warning;
  const showWarning = touched && !active && warning;

  const selectedOption = isMulti
    ? options.filter((option) => (value || []).includes(option.value))
    : options.find((option) => option.value === value);
  const selectedLabel =
    isMulti && selectedOption.length > 1
      ? multipleSelectedMessage
      : get(isMulti ? selectedOption[0] : selectedOption, 'label');
  let selectRef;
  const handleClearClick = () => {
    onChange(isMulti ? [] : '');
    if (selectRef) {
      selectRef.focus();
    }
  };

  const customDropdownIndicator = (props) => (
    <>
      {props.isFocused ? (
        <Button
          className="close-icon"
          dataId="selectClearButton"
          attributes={{
            'aria-label': 'clear',
            onTouchStart: handleClearClick,
            onMouseDown: handleClearClick,
            onKeyDown: (e) => {
              if (e.keyCode === 32) {
                handleClearClick();
              }
            },
          }}
        >
          <Close focusable={false} />
        </Button>
      ) : (
        <components.DropdownIndicator {...props}>
          <ChevronDown />
        </components.DropdownIndicator>
      )}
    </>
  );

  return (
    <div
      className={classNames('select-section', {
        'error-snap': error,
        'validation-error': showError,
        'strong-select-section-disabled': strong && disabled,
      })}
      title={title}
      data-id={dataId}
      aria-controls="listbox1"
    >
      <SelectDropdown
        {...reactSelectProps}
        alt={alt}
        aria-label={ariaLabel || placeholder}
        blurInputOnSelect={blurInputOnSelect}
        className={classNames('select-custom-control', {
          'has-placeholder': placeholder,
          'validation-error': showError,
          required: required && !value,
          'strong-select': strong,
          'read-only': readOnly,
        })}
        classNamePrefix="select"
        closeMenuOnSelect={closeMenuOnSelect}
        components={{
          DropdownIndicator: appearanceInputText ? customDropdownIndicator : DropdownIndicator,
          Option: isMulti ? CheckboxOption : Option,
          SingleValue,
          MultiValue: MultiValue(multipleSelectedMessage),
        }}
        defaultValue={selectedOption || null}
        filterOption={createFilter({ matchFrom })}
        id="listbox1"
        inputId={inputId}
        isClearable={isClearable}
        isDisabled={readOnly || disabled}
        isMulti={isMulti}
        isSearchable={isSearchable}
        noOptionsMessage={noOptionsMessage}
        onBlur={() => {
          setIsDropdownOpen(false);
          if (typeof onBlur === 'function') {
            onBlur();
          }
        }}
        onChange={(option) => {
          if (typeof onChange === 'function') {
            if (isMulti && Array.isArray(option)) {
              onChange(option.map((opt) => get(opt, 'value')));
            } else {
              onChange(option ? option.value : '');
            }
          }
        }}
        onFocus={() => {
          if (typeof onFocus === 'function') {
            onFocus();
          }
        }}
        onInputChange={(changeValue) => {
          if (typeof onKeyUp === 'function') {
            onKeyUp(changeValue);
          }
        }}
        onMenuOpen={() => {
          setIsDropdownOpen(true);
          if (typeof onMenuOpen === 'function') {
            onMenuOpen();
          }
        }}
        openMenuOnFocus={openMenuOnFocus}
        options={options}
        placeholder={helperLabel}
        styles={{
          noOptionsMessage: (baseStyles) => ({
            ...baseStyles,
            color: 'hsl(0, 0%, 30%)',
          }),
        }}
        ref={(r) => {
          selectRef = r;
        }}
        tabSelectsValue={tabSelectsValue}
        value={selectedOption || null}
        valueSeparator={valueSeparator}
      />
      <span
        className={classNames('form-control-placeholder', {
          'float-label': !!selectedLabel || active,
          required: required && !value,
        })}
      >
        {placeholder && required ? requiredLabel(placeholder) : placeholder}
      </span>
      {showError && !hideError && (
        <FieldValidationError
          errorCode={error}
          errorCodes={errorCodes}
          errorMessages={errorMessages}
          tabIndex={validationErrorTabIndex}
        />
      )}
      {showWarning && !hideError && (
        <FieldValidationError
          errorCode={warning}
          errorCodes={errorCodes}
          errorMessages={errorMessages}
          tabIndex={validationErrorTabIndex}
        />
      )}
    </div>
  );
};

Select.propTypes = {
  alt: PropTypes.string,
  appearanceInputText: PropTypes.bool,
  ariaLabel: PropTypes.string,
  blurInputOnSelect: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  dataId: PropTypes.string,
  disabled: PropTypes.bool,
  errorCodes: PropTypes.shape({}),
  errorMessages: PropTypes.errorMessages,
  helperLabel: PropTypes.string,
  hideError: PropTypes.bool,
  input: PropTypes.shape({
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyUp: PropTypes.func,
    value: PropTypes.any,
  }),
  inputId: PropTypes.string,
  isClearable: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  matchFrom: PropTypes.oneOf(['any', 'start']),
  meta: PropTypes.shape({
    active: PropTypes.bool,
    error: PropTypes.string,
    touched: PropTypes.bool,
    warning: PropTypes.string,
  }),
  multipleSelectedMessage: PropTypes.string,
  noOptionsMessage: PropTypes.node,
  onMenuOpen: PropTypes.func,
  openMenuOnFocus: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  placeholder: PropTypes.string,
  reactSelectProps: PropTypes.any,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  strong: PropTypes.bool,
  tabSelectsValue: PropTypes.bool,
  title: PropTypes.string,
  validationErrorTabIndex: PropTypes.number,
  valueSeparator: PropTypes.string,
};

Select.defaultProps = {
  appearanceInputText: false,
  ariaLabel: '',
  blurInputOnSelect: false,
  closeMenuOnSelect: true,
  dataId: undefined,
  disabled: false,
  errorCodes: {},
  errorMessages: {},
  helperLabel: null,
  hideError: false,
  input: {},
  isClearable: false,
  isMulti: false,
  isSearchable: false,
  matchFrom: 'any',
  meta: {},
  multipleSelectedMessage: 'Multiple Selected',
  noOptionsMessage: undefined,
  onMenuOpen: () => null,
  openMenuOnFocus: false,
  options: [],
  placeholder: null,
  readOnly: false,
  required: false,
  strong: false,
  tabSelectsValue: true,
  title: '',
  validationErrorTabIndex: 0,
  valueSeparator: ' \u2013 ',
};

export default Select;
